import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getImageUrl } from '../lib/utils-img';
import CornerOrnament from '../components/CornerOrnament';
import SEO from '../components/SEO';
import { useContent } from '../lib/content';
import Editable from '../components/Editable';
import API from '../lib/api';

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { get } = useContent('locations');

  useEffect(() => { API.get('/locations').then(r => { setLocations(r.data.locations); setLoading(false); }).catch(() => setLoading(false)); }, []);

  return (
    <div data-testid="locations-page" className="bg-[#101010] min-h-screen">
      <SEO
        title="Luxury Rental Locations in Ontario"
        path="/locations"
        description="Browse our premium luxury rental locations across Ontario."
        keywords="luxury rental locations Ontario, Toronto luxury rentals, Mississauga vacation rentals"
      />
      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gold text-[11px] uppercase tracking-[0.4em] font-bold mb-5">
            <Editable page="locations" field="hero_label">{get('hero_label')}</Editable>
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-bold mb-5">
            <Editable page="locations" field="hero_title">{get('hero_title')}</Editable>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Editable page="locations" field="hero_description" as="p" className="text-white/45 text-base sm:text-lg max-w-xl mx-auto">{get('hero_description')}</Editable>
          </motion.div>
        </div>
      </section>
      <section className="pb-28 lg:pb-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{[...Array(4)].map((_, i) => <div key={i} className="h-80 bg-[#1c1c1c] rounded-xl animate-pulse" />)}</div>
          ) : locations.length === 0 ? (
            <p className="text-center text-white/45 py-20 text-lg">No locations available yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {locations.map((loc, i) => (
                <motion.div key={loc.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}>
                  <Link to={`/locations/${loc.slug}`} className="group relative block overflow-hidden rounded-xl aspect-[16/10]" data-testid={`location-card-${loc.slug}`}>
                    <img src={getImageUrl(loc.hero_image || loc.thumbnail_image)} alt={loc.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <CornerOrnament size={24} offset="12px" color="rgba(255,255,255,0.1)" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <h3 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-2">{loc.name}</h3>
                      <p className="text-white/55 text-sm line-clamp-2 mb-4 max-w-sm">{loc.short_description}</p>
                      <span className="inline-flex items-center gap-2 text-[10px] text-gold uppercase tracking-[0.2em] font-bold group-hover:gap-4 transition-all duration-300">
                        View Properties <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
